/**
 * One Settings Studio session may add several history entries while a person
 * moves between categories. `history.back()` therefore cannot mean "close": it
 * only returns to the previous settings page. The launcher records the entry
 * immediately before Settings, and Close pops the whole settings run in one
 * browser-history operation so TanStack can restore the prior scroll position.
 */
const KEY = 'onmarble.settings.return.v1'

interface SettingsReturn {
  index: number
  href: string
}

function historyIndex(): number | null {
  const value = (window.history.state as { __TSR_index?: unknown } | null)?.__TSR_index
  return typeof value === 'number' && Number.isInteger(value) ? value : null
}

export function rememberSettingsReturn(): void {
  const index = historyIndex()
  if (index === null) return
  const href = `${window.location.pathname}${window.location.search}${window.location.hash}`
  window.sessionStorage.setItem(KEY, JSON.stringify({ index, href } satisfies SettingsReturn))
}

export function settingsReturnDelta(): number | null {
  const raw = window.sessionStorage.getItem(KEY)
  if (raw === null) return null
  try {
    const value = JSON.parse(raw) as Partial<SettingsReturn>
    const current = historyIndex()
    if (current === null || !Number.isInteger(value.index) || typeof value.href !== 'string') return null
    if (!value.href.startsWith('/') || value.href.startsWith('//') || value.href.startsWith('/settings')) return null
    const delta = (value.index as number) - current
    return delta < 0 ? delta : null
  } catch {
    return null
  }
}

export function clearSettingsReturn(): void {
  window.sessionStorage.removeItem(KEY)
}
